import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  CircularProgress
} from '@mui/material';
import { Annotation } from '../types';

interface AddAnnotationDialogProps {
  open: boolean;
  loading: boolean;
  annotationForm: {
    label: string;
    orderIndex: number;
  };
  onClose: () => void;
  onFormChange: (form: { label: string; orderIndex: number }) => void;
  onSave: () => void;
}

interface EditAnnotationDialogProps {
  open: boolean;
  loading: boolean;
  editAnnotationForm: {
    label: string;
    orderIndex: number;
  };
  onClose: () => void;
  onFormChange: (form: { label: string; orderIndex: number }) => void;
  onSave: () => void;
}

interface ReplaceImageDialogProps {
  open: boolean;
  uploading: boolean;
  selectedAnnotation: Annotation | null;
  onClose: () => void;
  onConfirm: () => void;
}

interface ReplaceMacroDialogProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const AddAnnotationDialog: React.FC<AddAnnotationDialogProps> = ({
  open,
  loading,
  annotationForm,
  onClose,
  onFormChange,
  onSave
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add Annotation</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, width: 400, maxWidth: '100%' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Annotation numbers are assigned sequentially across all macro images in this session.
              If for any reason duplicates are present, resolve manually now.
            </Typography>
          </Alert>
          <TextField
            label="Label"
            fullWidth
            margin="normal"
            value={annotationForm.label}
            onChange={(e) => onFormChange({
              ...annotationForm,
              label: e.target.value
            })}
            helperText="e.g., Mole 1, Mole 2, etc. (numbered across entire session)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onSave}
          disabled={loading || !annotationForm.label}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EditAnnotationDialog: React.FC<EditAnnotationDialogProps> = ({
  open,
  loading,
  editAnnotationForm,
  onClose,
  onFormChange,
  onSave
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Annotation</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, width: 400, maxWidth: '100%' }}>
          <TextField
            label="Label"
            fullWidth
            margin="normal"
            value={editAnnotationForm.label}
            onChange={(e) => onFormChange({
              ...editAnnotationForm,
              label: e.target.value
            })}
          />
          <TextField
            label="Order Index"
            type="number"
            fullWidth
            margin="normal"
            value={editAnnotationForm.orderIndex}
            onChange={(e) => onFormChange({
              ...editAnnotationForm,
              orderIndex: parseInt(e.target.value) || 0
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onSave}
          disabled={loading || !editAnnotationForm.label}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ReplaceImageDialog: React.FC<ReplaceImageDialogProps> = ({
  open,
  uploading,
  selectedAnnotation,
  onClose,
  onConfirm
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Replace Dermatoscopic Image</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will permanently delete the current dermatoscopic image and replace it with the new one.
        </Alert>
        <Typography>
          Are you sure you want to replace the existing dermatoscopic image for "{selectedAnnotation?.label}"?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="warning"
          onClick={onConfirm}
          disabled={uploading}
        >
          {uploading ? (
            <>
              Replacing...
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </>
          ) : (
            'Replace Image'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ReplaceMacroDialog: React.FC<ReplaceMacroDialogProps> = ({
  open,
  loading,
  onClose,
  onConfirm
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Replace Macro Image</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will permanently delete the current macro image and ALL associated annotations and dermatoscopic images.
        </Alert>
        <Typography>
          Are you sure you want to replace this macro image?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone. All annotations and linked dermatoscopic images will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="warning"
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? (
            <>
              Replacing...
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </>
          ) : (
            'Replace Image'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
